import { useCallback } from 'react';
import { useMemo } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';

import { IoIosArrowBack } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import { toast } from 'react-toastify';

import { Layout } from '~/components';
import Button from '~/components/Button2';
import { ExLibraryModal } from '~/components/ExLibraryModal';
import api from '~/services/api';

import { Card } from './Card';
import Modal from './Modal';

import {
  Header,
  HeaderWrapper,
  HeaderLabel,
  HeaderCount,
  Container,
  Navigator,
  NavigatorTitle,
  AlertContainer,
  ExercisesNotFoundMessage,
  DeleteExerciseContainer,
  AdminExercisesButton,
} from './styles';

type ExerciseData = {
  name: string;
  id: number;
};

type Exercise = {
  data: ExerciseData[];
  lastPage: number;
  page: number;
  perPage: number;
  total: string;
};

type ExEditorsRequestData = {
  muscle_segment: string;
  exercicies: Array<{
    name: string;
    id: number;
  }>;
};

type TrainingStoreRequestData = {
  description: string;
};

function ExLibrary() {
  const { goBack } = useHistory();
  const [activeScreen, setActiveScreen] = useState('main');
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [isAdminExercises, setIsAdminExercises] = useState<boolean>(false);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  function MainScreen() {
    const [loading, setLoading] = useState(false);
    const [segments, setSegments] = useState<string[]>([]);

    // Load exercises segments to fill checkbox options
    const loadSegments = useCallback(async () => {
      const { data } = await api.get<TrainingStoreRequestData[]>(
        `training-stores`,
        {
          params: {
            segment: `SEGMENTO`,
          },
        },
      );

      const mappedData = data.map((segment) => segment.description);

      setSegments(mappedData);
      setLoading(false);
    }, []);

    const handleHeaderClickFunc = useCallback(() => {
      if (isAdminExercises) {
        setIsAdminExercises(false);
      } else {
        goBack();
      }
    }, [isAdminExercises]);

    useEffect(() => {
      setLoading(true);
      loadSegments();
    }, [loadSegments]);

    const mainScreenCardsMemo = useMemo(
      () =>
        segments.length ? (
          segments.map((segment_) => (
            <Navigator
              onClick={() => {
                setActiveScreen(`segment`);
                setSelectedSegment(segment_);
              }}
            >
              <NavigatorTitle>{segment_}</NavigatorTitle>
              <IoIosArrowBack size={28} />
            </Navigator>
          ))
        ) : (
          <ExercisesNotFoundMessage>
            Nenhum segmento cadastrado.
          </ExercisesNotFoundMessage>
        ),
      [segments],
    );

    return (
      <>
        <Header>
          <HeaderWrapper onClick={handleHeaderClickFunc}>
            <IoIosArrowBack color="#FFDF22" size={24} />
            <HeaderLabel>
              {isAdminExercises
                ? 'Exercícios do Administrador'
                : 'Ex - Library'}
            </HeaderLabel>
          </HeaderWrapper>

          {!isAdminExercises && (
            <HeaderWrapper>
              <AdminExercisesButton
                disabled={loading}
                onClick={() => setIsAdminExercises(!isAdminExercises)}
              >
                Exercícios do Administrador
              </AdminExercisesButton>
            </HeaderWrapper>
          )}
        </Header>

        <Container>
          {loading ? (
            <AlertContainer>
              <MoonLoader size={30} color="#FFDF22" />
            </AlertContainer>
          ) : (
            mainScreenCardsMemo
          )}
        </Container>
      </>
    );
  }

  function Segment() {
    const [loading, setLoading] = useState(false);
    const [exercises, setExercises] = useState<Exercise>({} as Exercise);
    const [selectedExercise, setSelectedExercise] = useState<ExerciseData>(
      {} as ExerciseData,
    );
    const [showModal, setShowModal] = useState(false);

    const loadExercises = useCallback(async () => {
      const url = isAdminExercises ? '/ex-editors/admin/all' : '/ex-editors';
      const { data } = await api.get<ExEditorsRequestData[]>(url, {
        params: {
          perpage: 9999,
          muscle_segment: selectedSegment,
        },
      });
      data.length && setExercises(data[0].exercicies as unknown as Exercise);

      setLoading(false);
    }, []);

    useEffect(() => {
      setLoading(true);

      loadExercises();
    }, [loadExercises]);

    const handleDeletePackage = useCallback(async () => {
      try {
        await api.delete(`ex-editors/${selectedExercise.id}`);

        toast.success(`Exercício excluído com sucesso!`);

        setShowModal(false);

        loadExercises();
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          'Houve um erro ao deletar o exercício';
        toast.error(message);
      }
    }, [selectedExercise, loadExercises]);

    function DeleteExerciseModal() {
      return (
        <Modal isOpen={showModal} setOpen={setShowModal}>
          <DeleteExerciseContainer>
            <h1>Atenção</h1>

            <p>
              Tem certeza que deseja excluir o exercício &quot;
              {selectedExercise.name}&quot;?
            </p>

            <div>
              <button type="button" onClick={() => handleDeletePackage()}>
                Sim
              </button>

              <button type="button" onClick={() => setShowModal(false)}>
                Não
              </button>
            </div>
          </DeleteExerciseContainer>
        </Modal>
      );
    }

    const cardsMemo = useMemo(
      () =>
        exercises?.data?.length ? (
          exercises?.data?.map((exercise) => (
            <Card
              key={exercise.id}
              select={setSelectedExercise}
              deleteModal={setShowModal}
              isAdminExercise={isAdminExercises}
              exercise={{ ...exercise, muscle_segment: selectedSegment }}
            />
          ))
        ) : (
          <ExercisesNotFoundMessage>
            Nenhum exercício cadastrado.
          </ExercisesNotFoundMessage>
        ),
      [exercises],
    );

    const ExercisesQuantityText = useMemo(
      () =>
        `${exercises?.data?.length || 0} ${
          exercises?.data?.length === 1 ? 'exercício' : 'exercícios'
        }`,
      [exercises],
    );

    return (
      <>
        <DeleteExerciseModal />

        <Header
          style={{
            justifyContent: isAdminExercises ? 'flex-start' : 'space-between',
          }}
        >
          <HeaderWrapper onClick={() => setActiveScreen('main')}>
            <IoIosArrowBack color="#FFDF22" size={24} />
            <HeaderLabel>{selectedSegment}</HeaderLabel>
          </HeaderWrapper>

          <HeaderCount style={{ marginLeft: isAdminExercises ? '25%' : '0' }}>
            {ExercisesQuantityText}
          </HeaderCount>
          {!isAdminExercises && (
            <Button
              variant="border"
              extraStyles={{
                borderColor: '#FFDF22',
                backgroundColor: '#FFDF22',
                width: '15%',
              }}
              onClick={() => setModalIsOpen(true)}
              textContent="Cadastrar"
              textColor="#000"
            />
          )}
        </Header>

        <Container>
          {loading ? (
            <AlertContainer>
              <MoonLoader size={30} color="#FFDF22" />
            </AlertContainer>
          ) : (
            cardsMemo
          )}
        </Container>
        <ExLibraryModal
          isRegisteringExercise
          modalVisible={modalIsOpen}
          categoryName={selectedSegment}
          setModalVisible={setModalIsOpen}
          title="CADASTRAR NOVO EXERCÍCIO"
          modalType="addCategoryExercise"
        />
      </>
    );
  }

  const componentSelected: {
    [key: string]: { title: string; component: JSX.Element };
  } = {
    main: {
      title: 'Main',
      component: <MainScreen />,
    },
    segment: {
      title: 'segment',
      component: <Segment />,
    },
  };

  return (
    <Layout title="I.LAB">{componentSelected[activeScreen].component}</Layout>
  );
}

export default ExLibrary;
